import { useState, useEffect, useRef, useMemo } from "react";
import { Search, Loader2, X, TrendingUp, Sparkles, Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useCurrency } from '@/contexts/MarketContext';
import { useDebounce } from "@/hooks/use-debounce";
import { cn, singularizeCategory } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useBrands } from "@/hooks/use-cms-api";

// Helper function to highlight matching text
const highlightText = (text: string, query: string) => {
  if (!query.trim() || !text) return text;
  
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  
  return (
    <>
      {parts.map((part, index) => 
        part.toLowerCase() === query.toLowerCase() ? (
          <strong key={index} className="font-bold text-primary">{part}</strong>
        ) : (
          part
        )
      )}
    </>
  );
};

interface Product {
  id: string;
  title: string;
  description: string;
  image_url: string;
  price: number;
  currency: string;
  slug: string;
  category: string;
  brand_name?: string;
}

interface SearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SearchModal = ({ open, onOpenChange }: SearchModalProps) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [trendingSearches, setTrendingSearches] = useState<Array<{ query: string; search_count: number }>>([]);
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState("");
  const [selectedBrand, setSelectedBrand] = useState<string>("all");
  const debouncedQuery = useDebounce(query, 300);
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const { toast } = useToast();
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Fetch brands from CMS
  const { data: brands } = useBrands();

  const placeholderTexts = [
    "Search for supplements...",
    "Find vitamins...",
    "Discover wellness products...",
    "Search omega-3...",
    "Find probiotics...",
  ];

  // Animated placeholder typing effect
  useEffect(() => {
    let textIndex = 0;
    let charIndex = 0;
    let isDeleting = false;
    let timeout: NodeJS.Timeout;

    const animatePlaceholder = () => {
      const currentText = placeholderTexts[textIndex];
      
      if (!isDeleting) {
        setAnimatedPlaceholder(currentText.substring(0, charIndex + 1));
        charIndex++;
        
        if (charIndex === currentText.length) {
          timeout = setTimeout(() => {
            isDeleting = true;
            animatePlaceholder();
          }, 2000);
          return;
        }
        
        timeout = setTimeout(animatePlaceholder, 100);
      } else {
        setAnimatedPlaceholder(currentText.substring(0, charIndex - 1));
        charIndex--;
        
        if (charIndex === 0) {
          isDeleting = false;
          textIndex = (textIndex + 1) % placeholderTexts.length;
          timeout = setTimeout(animatePlaceholder, 500);
          return;
        }
        
        timeout = setTimeout(animatePlaceholder, 50);
      }
    };

    animatePlaceholder();

    return () => clearTimeout(timeout);
  }, []);

  // Load recent searches and trending searches
  useEffect(() => {
    const stored = localStorage.getItem('recentSearches');
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse recent searches:', e);
      }
    }

    // Load trending searches
    const loadTrendingSearches = async () => {
      try {
        const { data, error } = await supabase.rpc('get_trending_searches', {
          days_back: 7,
          result_limit: 6
        });

        if (error) throw error;
        if (data && data.length > 0) {
          setTrendingSearches(data);
        }
      } catch (error) {
        console.error('Failed to load trending searches:', error);
      }
    };

    loadTrendingSearches();
  }, []);

  // Save search to recent searches
  const addToRecentSearches = (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.length < 2) return;
    
    const updated = [
      searchQuery,
      ...recentSearches.filter(s => s.toLowerCase() !== searchQuery.toLowerCase())
    ].slice(0, 5); // Keep only last 5 searches
    
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  // Clear all recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  // Remove single recent search
  const removeRecentSearch = (searchToRemove: string) => {
    const updated = recentSearches.filter(s => s !== searchToRemove);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [open, onOpenChange]);

  // Track search analytics
  const trackSearch = async (searchQuery: string, resultsCount: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      await supabase.from('search_analytics').insert({
        query: searchQuery.trim(),
        user_id: user?.id || null,
        results_count: resultsCount
      });
    } catch (error) {
      console.error('Failed to track search:', error);
    }
  };

  // Search products when query changes
  useEffect(() => {
    const searchProducts = async () => {
      if (!debouncedQuery || debouncedQuery.trim().length < 2) {
        setResults([]);
        return;
      }

      setIsLoading(true);

      try {
        const { data, error } = await supabase.functions.invoke('search-products', {
          body: { query: debouncedQuery }
        });

        if (error) throw error;

        const products = data?.products || [];
        setResults(products);
        
        // Track the search
        trackSearch(debouncedQuery, products.length);
        
        // Add to recent searches when results are shown
        if (products.length > 0) {
          addToRecentSearches(debouncedQuery);
        }
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    searchProducts();
  }, [debouncedQuery]);

  // Reset when dropdown closes
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
      setSelectedBrand("all");
    }
  }, [open]);

  // Filter results by selected brand
  const filteredResults = useMemo(() => {
    if (selectedBrand === "all") return results;
    return results.filter(product => product.brand_name === selectedBrand);
  }, [results, selectedBrand]);

  // Get unique brands from results for dynamic filtering
  const resultBrands = useMemo(() => {
    const uniqueBrands = new Set<string>();
    results.forEach(product => {
      if (product.brand_name) uniqueBrands.add(product.brand_name);
    });
    return Array.from(uniqueBrands).sort();
  }, [results]);

  const handleProductClick = (slug: string) => {
    onOpenChange(false);
    navigate(`/product/${slug}`);
  };

  const handlePopularSearch = (search: string) => {
    setQuery(search);
  };

  const handleRecentSearch = (search: string) => {
    setQuery(search);
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 animate-fade-in"
        onClick={() => onOpenChange(false)}
      />
      
      {/* Dropdown */}
      <div 
        ref={dropdownRef}
        className="fixed left-0 right-0 top-[64px] z-50 animate-slide-in-right"
      >
        <div className="container mx-auto px-4 md:px-8">
          <Card className="w-full max-w-4xl mx-auto shadow-2xl border-2">
            {/* Close Button */}
            <button
              onClick={() => onOpenChange(false)}
              className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-accent transition-colors"
              aria-label="Close search"
            >
              <X className="h-5 w-5" />
            </button>
            
            {/* Search Input & Brand Filter */}
            <div className="px-6 pt-6 pb-4 border-b bg-gradient-to-br from-card via-card to-muted/30">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-primary" />
                  <Input
                    type="text"
                    placeholder={animatedPlaceholder}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="pl-12 pr-12 h-14 text-base md:text-lg border-2 focus-visible:ring-primary bg-background"
                    autoFocus
                  />
                  {query && (
                    <button
                      onClick={() => setQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  )}
                </div>
                
                {/* Brand Filter Dropdown */}
                <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                  <SelectTrigger className="h-14 w-full md:w-[200px] border-2 bg-background">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <SelectValue placeholder="All Brands" />
                    </div>
                  </SelectTrigger>
                  <SelectContent className="bg-background border-2 z-[60]">
                    <SelectItem value="all">All Brands</SelectItem>
                    {(resultBrands.length > 0 ? resultBrands : (brands?.map(b => b.name) || [])).map((brand) => (
                      <SelectItem key={brand} value={brand}>
                        {brand}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[70vh] px-6 pb-6 bg-background">
              {!query && (
                <div className="py-6 space-y-6 animate-fade-in">
                  {/* Recent Searches */}
                  {recentSearches.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Search className="h-4 w-4 text-primary" />
                          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                            Recent Searches
                          </h3>
                        </div>
                        <button
                          onClick={clearRecentSearches}
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Clear all
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {recentSearches.map((search) => (
                          <Badge
                            key={search}
                            variant="outline"
                            className="cursor-pointer hover:bg-accent transition-colors px-4 py-2 text-sm group relative pr-8"
                            onClick={() => handleRecentSearch(search)}
                          >
                            {search}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeRecentSearch(search);
                              }}
                              className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Trending Searches */}
                  {trendingSearches.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="h-4 w-4 text-primary" />
                        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                          Trending Searches
                        </h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {trendingSearches.map((search) => (
                          <Badge
                            key={search.query}
                            variant="secondary"
                            className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors px-4 py-2 text-sm group relative"
                            onClick={() => handlePopularSearch(search.query)}
                          >
                            {search.query}
                            <span className="ml-2 text-xs opacity-60">
                              {search.search_count}
                            </span>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-gradient-to-br from-primary/10 via-accent/10 to-primary/5 rounded-lg p-6 border border-primary/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold">AI-Powered Search</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Our smart search understands natural language. Try searching for health goals, ingredients, or specific supplements.
                    </p>
                  </div>
                </div>
              )}

              {query.length >= 2 && (
                <div className="py-4">
                  {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-12 animate-fade-in">
                      <Loader2 className="h-10 w-10 animate-spin text-primary mb-3" />
                      <span className="text-sm text-muted-foreground">Searching for the best matches...</span>
                    </div>
                  ) : filteredResults.length > 0 ? (
                    <div className="space-y-3 animate-fade-in">
                      <div className="flex items-center justify-between mb-4">
                        <p className="text-sm text-muted-foreground">
                          Found <span className="font-semibold text-foreground">{filteredResults.length}</span> result{filteredResults.length !== 1 ? 's' : ''}
                          {selectedBrand !== "all" && (
                            <span className="ml-1">from <span className="font-medium text-foreground">{selectedBrand}</span></span>
                          )}
                        </p>
                        {selectedBrand !== "all" && (
                          <button
                            onClick={() => setSelectedBrand("all")}
                            className="text-xs text-primary hover:underline"
                          >
                            Clear filter
                          </button>
                        )}
                      </div>
                      {filteredResults.map((product, index) => (
                        <Card
                          key={product.id}
                          onClick={() => handleProductClick(product.slug)}
                          className={cn(
                            "cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] border-2 hover:border-primary/50",
                            "animate-fade-in"
                          )}
                          style={{ animationDelay: `${index * 50}ms` }}
                        >
                          <div className="flex gap-4 p-4">
                            {product.image_url && (
                              <div className="relative flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-muted">
                                <img
                                  src={product.image_url}
                                  alt={product.title}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              {product.brand_name && (
                                <p className="text-xs text-muted-foreground font-medium mb-0.5">
                                  {product.brand_name}
                                </p>
                              )}
                              <h4 className="font-semibold text-base line-clamp-1 mb-1">
                                {highlightText(product.title, query)}
                              </h4>
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                                {highlightText(product.description, query)}
                              </p>
                              <div className="flex items-center gap-2">
                                <span className="text-lg font-bold text-primary">
                                  {formatPrice(product.price, product.currency)}
                                </span>
                                {product.category && (
                                  <Badge variant="secondary" className="text-xs">
                                    {singularizeCategory(product.category)}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : results.length > 0 && selectedBrand !== "all" ? (
                    <div className="py-12 text-center animate-fade-in">
                      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                        <Building2 className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <p className="text-base font-medium mb-2">No results from "{selectedBrand}"</p>
                      <p className="text-sm text-muted-foreground mb-4">
                        Try selecting a different brand or clear the filter
                      </p>
                      <button
                        onClick={() => setSelectedBrand("all")}
                        className="text-sm text-primary hover:underline"
                      >
                        Show all {results.length} results
                      </button>
                    </div>
                  ) : (
                    <div className="py-12 text-center animate-fade-in">
                      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                        <Search className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <p className="text-base font-medium mb-2">No results found for "{query}"</p>
                      <p className="text-sm text-muted-foreground">
                        Try different keywords or browse our catalog
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};
